import React, { useState, useMemo, useRef, useEffect } from "react";
import { 
  TrendingUp, Globe, Info, Target, 
  ChevronDown, AlertCircle, Search, Zap, HelpCircle
} from "lucide-react";

const globalStyles = `
  html, body { margin: 0; padding: 0; overflow: hidden; height: 100vh; width: 100vw; background-color: #020617; color: #f1f5f9; }
  #root { height: 100%; width: 100%; }
  ::-webkit-scrollbar { display: none; }
  * { -ms-overflow-style: none; scrollbar-width: none; box-sizing: border-box; }
  .custom-input:focus { border-color: #38BDF8 !important; outline: none; box-shadow: 0 0 0 1px #38BDF8; }
  .tooltip-trigger:hover .tooltip-box { display: block !important; }
`;

const majors = ["EUR", "GBP", "AUD", "NZD", "USD", "CAD", "CHF", "JPY"];
const generateForexPairs = () => {
  const pairs = {};
  majors.forEach(base => {
    majors.forEach(quote => {
      if (base !== quote) pairs[`${base}/${quote}`] = { name: "Major Cross", val: 10 }; 
    });
  });
  return pairs;
};

const FUTURES_DATA = {
  "Equity Indices": {
    "NQ E-mini": { name: "Nasdaq 100", val: 20 },
    "NQ Micro": { name: "Micro Nasdaq", val: 2 },
    "ES E-mini": { name: "S&P 500", val: 50 },
    "ES Micro": { name: "Micro S&P", val: 5 },
    "YM E-mini": { name: "Dow Jones", val: 5 },
    "YM Micro": { name: "Micro Dow", val: 0.5 },
  },
  "Metals & Energy": {
    "GC Gold Std": { name: "Gold", val: 100 },
    "GC Gold Micro": { name: "Micro Gold", val: 10 },
    "SI Silver Std": { name: "Silver", val: 5000 },
    "CL Crude Oil": { name: "Crude Oil", val: 1000 },
  }
};

const FOREX_DATA = {
  "Metals (Forex)": {
    "XAU/USD": { name: "Gold (Spot)", val: 10 },
    "XAG/USD": { name: "Silver (Spot)", val: 50 },
  },
  "Major Pairs": generateForexPairs(),
};

export default function LotCalculate() {
  const [calcMode, setCalcMode] = useState("futures");
  const [subTab, setSubTab] = useState("position");
  const [balance, setBalance] = useState(50000);
  const [riskPct, setRiskPct] = useState(1);
  const [stopLoss, setStopLoss] = useState(10);
  const [selectedAsset, setSelectedAsset] = useState("NQ E-mini");
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleEvents = (e) => {
      if (e.key === "Escape") setIsDropdownOpen(false);
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsDropdownOpen(false);
        setSearchTerm("");
      }
    };
    document.addEventListener("mousedown", handleEvents);
    document.addEventListener("keydown", handleEvents);
    return () => {
      document.removeEventListener("mousedown", handleEvents);
      document.removeEventListener("keydown", handleEvents);
    };
  }, []);

  const currentData = calcMode === "futures" ? FUTURES_DATA : FOREX_DATA;

  const filteredData = useMemo(() => {
    if (!searchTerm) return currentData;
    const filtered = {};
    Object.entries(currentData).forEach(([cat, assets]) => {
      const matches = Object.entries(assets).filter(([symbol, details]) => 
        symbol.toLowerCase().includes(searchTerm.toLowerCase()) || 
        details.name.toLowerCase().includes(searchTerm.toLowerCase())
      );
      if (matches.length > 0) filtered[cat] = Object.fromEntries(matches);
    });
    return filtered;
  }, [searchTerm, currentData]);

  const results = useMemo(() => {
    const riskAmount = balance * (riskPct / 100);
    let assetVal = 10;
    Object.values(currentData).forEach(group => {
      if (group[selectedAsset]) assetVal = group[selectedAsset].val;
    });

    const rawSize = riskAmount / (stopLoss * assetVal);
    const finalSize = calcMode === "futures" ? Math.floor(rawSize) : Number(rawSize.toFixed(2));
    
    const actualRisk = (finalSize * stopLoss * assetVal);
    const tradesToLiquidation = finalSize > 0 ? Math.floor(balance / actualRisk) : 0;

    let riskLevel = { label: "Conservative", color: "#10b981", bg: "rgba(16, 185, 129, 0.1)" };
    if (riskPct > 1.5) riskLevel = { label: "Moderate", color: "#f59e0b", bg: "rgba(245, 158, 11, 0.1)" };
    if (riskPct > 3) riskLevel = { label: "High Risk", color: "#ef4444", bg: "rgba(239, 68, 68, 0.1)" };

    // Detection for Zero Size
    const isZero = finalSize <= 0;

    return {
      riskAmount: actualRisk.toLocaleString(),
      size: finalSize,
      label: calcMode === "futures" ? "Contracts" : "Lots",
      tick: assetVal,
      buffer: tradesToLiquidation,
      riskLevel,
      isZero
    };
  }, [balance, riskPct, stopLoss, selectedAsset, currentData, calcMode]);

  return (
    <div style={styles.container}>
      <style>{globalStyles}</style>

      <div style={styles.header}>
        <div style={styles.modeToggle}>
          <button style={calcMode === "futures" ? styles.modeBtnActive : styles.modeBtn} onClick={() => { setCalcMode("futures"); setSelectedAsset("NQ E-mini"); }}>
            <Zap size={16} /> FUTURES
          </button>
          <button style={calcMode === "forex" ? styles.modeBtnActive : styles.modeBtn} onClick={() => { setCalcMode("forex"); setSelectedAsset("XAU/USD"); }}>
            <Globe size={16} /> FOREX
          </button>
        </div>

        <div style={styles.tabGroup}>
          <button style={subTab === "position" ? styles.tabActive : styles.tab} onClick={() => setSubTab("position")}>Position</button>
          <button style={subTab === "compare" ? styles.tabActive : styles.tab} onClick={() => setSubTab("compare")}>Compare</button>
        </div>
      </div>

      {subTab === "position" ? (
        <>
          <div style={styles.grid}>
            <div style={styles.card}>
              <div style={styles.cardTop}>
                <span style={styles.cardLabel}>TRADE PARAMETERS</span>
                <AlertCircle size={14} color="#475569" />
              </div>

              <div style={{ position: "relative", marginBottom: "20px" }} ref={dropdownRef}>
                <label style={styles.fieldLabel}>Instrument</label>
                <div style={styles.selectBox} onClick={() => setIsDropdownOpen(!isDropdownOpen)}>
                  <span>{selectedAsset}</span>
                  <ChevronDown size={16} color="#38BDF8" />
                </div>
                {isDropdownOpen && (
                  <div style={styles.menu}>
                    <div style={styles.searchBar}>
                      <Search size={14} color="#475569" />
                      <input autoFocus placeholder="Search..." style={styles.menuInput} value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
                    </div>
                    <div style={styles.scroll}>
                      {Object.entries(filteredData).map(([cat, assets]) => (
                        <div key={cat}>
                          <div style={styles.catName}>{cat}</div>
                          {Object.keys(assets).map(a => (
                            <div key={a} style={styles.menuItem} onClick={() => { setSelectedAsset(a); setIsDropdownOpen(false); setSearchTerm(""); }}>
                              {a} <span style={{fontSize: 10, color: '#475569'}}>{assets[a].name}</span>
                            </div>
                          ))}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <div style={styles.inputRow}>
                <div style={{ flex: 1 }}>
                  <label style={styles.fieldLabel}>Account Balance ($)</label>
                  <input type="number" value={balance} onChange={(e) => setBalance(Number(e.target.value))} className="custom-input" style={styles.rawInput} />
                </div>
              </div>

              <div style={styles.inputRow}>
                <div style={{ flex: 1 }}>
                  <label style={styles.fieldLabel}>Risk (%)</label>
                  <input type="number" step="0.1" value={riskPct} onChange={(e) => setRiskPct(Number(e.target.value))} className="custom-input" style={styles.rawInput} />
                </div>
                <div style={{ flex: 1 }}>
                  <label style={styles.fieldLabel}>{calcMode === "futures" ? "Stop (Pts)" : "Stop (Pips)"}</label>
                  <input type="number" value={stopLoss} onChange={(e) => setStopLoss(Number(e.target.value))} className="custom-input" style={styles.rawInput} />
                </div>
              </div>
            </div>

            <div style={{...styles.displayCard, borderColor: results.isZero ? '#ef444455' : '#38BDF833'}}>
              <div style={styles.badgeRow}>
                <div style={{...styles.riskLevelBadge, color: results.riskLevel.color, background: results.riskLevel.bg}}>
                  {results.riskLevel.label}
                </div>
                <div style={{...styles.riskBadge, color: results.isZero ? '#94a3b8' : '#ef4444'}}>ACTUAL RISK: ${results.riskAmount}</div>
              </div>
              
              <div style={styles.center}>
                {results.isZero ? (
                  <div style={styles.zeroWarningBox}>
                    <AlertCircle size={32} color="#ef4444" style={{marginBottom: 12}} />
                    <div style={styles.zeroTitle}>SIZE TOO SMALL</div>
                    <div style={styles.zeroText}>
                      Your Stop Loss is too wide for your current Risk %. 
                      <br/><br/>
                      <span style={{color: '#38BDF8'}}>Adjust:</span> Increase Risk % or decrease Stop Loss points.
                    </div>
                  </div>
                ) : (
                  <>
                    <div style={styles.mainVal}>{results.size}</div>
                    <div style={styles.mainUnit}>{results.label}</div>
                  </>
                )}
              </div>

              <div style={{...styles.cardFooter, position: 'relative'}} className="tooltip-trigger">
                <HelpCircle size={14} color="#38BDF8" />
                <span>Survival Buffer: {results.buffer} trades</span>
                
                {/* TOOLTIP BOX */}
                <div className="tooltip-box" style={styles.tooltip}>
                  <div style={{fontWeight: '800', marginBottom: 4, color: '#38BDF8'}}>What is Survival Buffer?</div>
                  This shows how many consecutive losing trades you can survive before your balance is depleted at this current risk level.
                </div>
              </div>
            </div>
          </div>

          <div style={styles.targetSection}>
            <div style={styles.targetHeader}><Target size={16} /> PROFIT TARGETS (R:R)</div>
            <div style={styles.targetGrid}>
              {[1, 2, 3].map(ratio => (
                <div key={ratio} style={styles.targetCard}>
                  <div style={styles.targetLabel}>1:{ratio} Reward</div>
                  <div style={styles.targetVal}>+${(parseFloat(results.riskAmount.replace(/,/g, '')) * ratio).toLocaleString()}</div>
                  <div style={styles.targetSub}>{stopLoss * ratio} {calcMode === "futures" ? "Points" : "Pips"}</div>
                </div>
              ))}
            </div>
          </div>
        </>
      ) : (
        <div style={styles.compareWrapper}>
          <table style={styles.table}>
            <thead style={styles.thead}>
              <tr>
                <th style={styles.th}>Asset</th>
                <th style={styles.th}>Tick Value</th>
                <th style={styles.th}>Size (1% Risk)</th>
                <th style={styles.th}>Total Risk</th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(currentData).map(([cat, assets]) => 
                Object.entries(assets).map(([id, info]) => (
                  <tr key={id} style={styles.tr}>
                    <td style={styles.tdActive}>{id}</td>
                    <td style={styles.td}>${info.val}</td>
                    <td style={styles.tdHighlight}>
                      {calcMode === "futures" ? Math.floor((balance * 0.01) / (stopLoss * info.val)) : ((balance * 0.01) / (stopLoss * info.val)).toFixed(2)}
                    </td>
                    <td style={styles.td}>${(balance * 0.01).toLocaleString()}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

const styles = {
  container: { padding: "40px", maxWidth: "1200px", margin: "0 auto" },
  header: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "32px" },
  modeToggle: { display: "flex", background: "#0d1117", padding: "4px", borderRadius: "12px", border: "1px solid #1e293b" },
  modeBtn: { background: "transparent", color: "#64748b", border: "none", padding: "10px 24px", borderRadius: "8px", cursor: "pointer", fontWeight: "600", fontSize: "12px", display: "flex", alignItems: "center", gap: "8px" },
  modeBtnActive: { background: "#38BDF8", color: "#000", border: "none", padding: "10px 24px", borderRadius: "8px", cursor: "pointer", fontWeight: "700", fontSize: "12px", display: "flex", alignItems: "center", gap: "8px" },
  tabGroup: { display: "flex", gap: "24px" },
  tab: { background: "transparent", color: "#475569", border: "none", cursor: "pointer", fontSize: "14px", fontWeight: "600", padding: "8px 0" },
  tabActive: { background: "transparent", color: "#38BDF8", border: "none", borderBottom: "2px solid #38BDF8", cursor: "pointer", fontSize: "14px", fontWeight: "700", padding: "8px 0" },
  grid: { display: "grid", gridTemplateColumns: "1.1fr 1fr", gap: "24px", marginBottom: "24px" },
  card: { background: "#0d1117", border: "1px solid #1e293b", borderRadius: "20px", padding: "32px", height: "460px" },
  cardTop: { display: "flex", justifyContent: "space-between", marginBottom: "28px" },
  cardLabel: { color: "#475569", fontSize: "11px", fontWeight: "800", letterSpacing: "1.5px" },
  fieldLabel: { color: "#94a3b8", fontSize: "12px", marginBottom: "8px", display: "block", fontWeight: "500" },
  selectBox: { background: "#020617", border: "1px solid #1e293b", borderRadius: "12px", padding: "16px", color: "#fff", display: "flex", justifyContent: "space-between", alignItems: "center", cursor: "pointer" },
  menu: { position: "absolute", top: "100%", left: 0, width: "100%", background: "#0d1117", border: "1px solid #334155", borderRadius: "16px", zIndex: 1000, marginTop: "8px", overflow: "hidden", boxShadow: "0 20px 50px rgba(0,0,0,0.6)" },
  searchBar: { display: "flex", alignItems: "center", padding: "12px 16px", gap: "12px", borderBottom: "1px solid #1e293b" },
  menuInput: { background: "transparent", border: "none", color: "#fff", outline: "none", width: "100%", fontSize: "14px" },
  scroll: { maxHeight: "250px", overflowY: "auto" },
  catName: { padding: "12px 16px", fontSize: "10px", color: "#38BDF8", fontWeight: "900", background: "rgba(56,189,248,0.03)", textTransform: "uppercase" },
  menuItem: { padding: "14px 16px", color: "#94a3b8", cursor: "pointer", fontSize: "13px", borderBottom: "1px solid #1e293b", display: "flex", justifyContent: "space-between", transition: "0.2s" },
  inputRow: { display: "flex", gap: "20px", marginBottom: "24px" },
  rawInput: { width: "100%", background: "#020617", border: "1px solid #1e293b", borderRadius: "12px", padding: "16px", color: "#fff", fontWeight: "700", fontSize: "16px", transition: "0.2s" },
  displayCard: { background: "linear-gradient(145deg, #0d1117 0%, #020617 100%)", border: "1px solid #38BDF833", borderRadius: "20px", padding: "32px", display: "flex", flexDirection: "column", height: "460px", transition: '0.3s' },
  badgeRow: { display: "flex", justifyContent: "space-between", alignItems: "center" },
  riskLevelBadge: { padding: "6px 14px", borderRadius: "100px", fontSize: "10px", fontWeight: "900", textTransform: "uppercase" },
  riskBadge: { fontSize: "11px", fontWeight: "900" },
  center: { flex: 1, display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center", textAlign: 'center' },
  mainVal: { fontSize: "110px", fontWeight: "900", color: "#38BDF8", lineHeight: 0.8 },
  mainUnit: { fontSize: "14px", color: "#475569", letterSpacing: "6px", textTransform: "uppercase", marginTop: "24px", fontWeight: "700" },
  zeroWarningBox: { padding: "20px" },
  zeroTitle: { color: "#ef4444", fontWeight: "900", fontSize: "18px", letterSpacing: "1px", marginBottom: "8px" },
  zeroText: { color: "#64748b", fontSize: "13px", lineHeight: "1.5" },
  cardFooter: { display: "flex", alignItems: "center", gap: "12px", color: "#475569", fontSize: "12px", marginTop: "auto", cursor: 'help' },
  tooltip: { display: 'none', position: 'absolute', bottom: '100%', left: 0, width: '220px', background: '#1e293b', padding: '12px', borderRadius: '10px', fontSize: '11px', color: '#94a3b8', boxShadow: '0 10px 25px rgba(0,0,0,0.5)', marginBottom: '10px', zIndex: 10 },
  targetSection: { background: "#0d1117", border: "1px solid #1e293b", borderRadius: "20px", padding: "28px" },
  targetHeader: { display: "flex", alignItems: "center", gap: "10px", fontSize: "11px", fontWeight: "800", color: "#10b981", marginBottom: "20px" },
  targetGrid: { display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "20px" },
  targetCard: { background: "#020617", border: "1px solid #1e293b", borderRadius: "16px", padding: "20px", textAlign: "center" },
  targetLabel: { fontSize: "11px", color: "#475569", marginBottom: "8px" },
  targetVal: { fontSize: "24px", fontWeight: "900", color: "#10b981", marginBottom: "4px" },
  targetSub: { fontSize: "10px", color: "#475569" },
  compareWrapper: { background: "#0d1117", border: "1px solid #1e293b", borderRadius: "20px", padding: "32px", height: "calc(100vh - 200px)", overflowY: "auto" },
  table: { width: "100%", borderCollapse: "collapse" },
  thead: { borderBottom: "1px solid #1e293b" },
  th: { textAlign: "left", color: "#475569", fontSize: "12px", padding: "16px", textTransform: "uppercase", letterSpacing: "1px" },
  tr: { borderBottom: "1px solid #020617", transition: "0.2s" },
  td: { padding: "20px 16px", color: "#64748b", fontSize: "14px" },
  tdActive: { padding: "20px 16px", color: "#f1f5f9", fontWeight: "700", fontSize: "14px" },
  tdHighlight: { padding: "20px 16px", color: "#38BDF8", fontWeight: "900", fontSize: "15px" },
};